import { PageHeader, Skeleton } from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import type { ReactNode } from 'react';
import { useTenantDetail } from '../catalog/useCatalogQueries.ts';
import { useGeoAreas, type Geography } from '../geography/useGeoAreas.ts';
import { useActiveLocations } from '../inventory/useStockMatrix.ts';
import { LoadFailed } from '../LoadFailed.tsx';
import { usePageContext } from '../usePageContext.ts';
import { useCompanies, type Companies } from '../companies.ts';
import type { Depot } from './OrderForm.tsx';

export type OrderFormData = {
  readonly currency: string;
  readonly geography: Geography;
  readonly depots: readonly Depot[];
  readonly companies: Companies;
};

export type OrderFormFrameProps = {
  api: TypedClient;
  title: string;
  context: string;
  children: (data: OrderFormData) => ReactNode;
};

export const OrderFormFrame = ({ api, title, context, children }: OrderFormFrameProps) => {
  const { t, describeError } = usePageContext();
  const tenant = useTenantDetail(api);
  const geography = useGeoAreas(api);
  const depots = useActiveLocations(api);
  const companies = useCompanies(api);
  const failed = [tenant, geography, depots].find((query) => query.isError);

  const body = () => {
    if (failed !== undefined)
      return (
        <LoadFailed
          description={describeError(failed.error)}
          onRetry={() => {
            void tenant.refetch();
            void geography.refetch();
            void depots.refetch();
          }}
        />
      );
    if (tenant.data === undefined || geography.data === undefined || depots.data === undefined)
      return <Skeleton height="240px" label={t('shell.loading')} />;
    return children({
      currency: tenant.data.currency,
      geography: geography.data,
      depots: depots.data.items,
      companies,
    });
  };

  return (
    <>
      <PageHeader title={title} context={context} />
      {body()}
    </>
  );
};
